import { ChildProps } from '@apollo/client/react/hoc';
import { gql } from '@apollo/client';
import React from 'react';
import uuid from 'uuid/v4';
import { PAYPAL_CLIENT_ID } from '@reverbdotcom/env';
import { PayPalScriptProvider, PayPalButtons, ReactPayPalScriptOptions } from '@paypal/react-paypal-js';
import { OnApproveData } from '@paypal/paypal-js';
import I18n from 'i18n-js';
import {
  CoreCheckoutStartPaypalCheckout,
  core_apimessages_Error as Error,
  core_apimessages_StartPaypalCheckoutRequest_PaymentMethodType as PayPalPaymentMethodType,
} from '../../schemas/rql';
import { withGraphql } from '@reverbdotcom/commons/src/with_graphql';
import { useHOCMutation, MutationFunction } from '@reverbdotcom/commons/src/useHOCMutation';
import { connect } from '@reverbdotcom/commons/src/connect';
import Location from '../../lib/wrapped_location';
import { MParticleEventName } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { useEventTracker } from '../trackPayPalEvent';
import { buildCheckoutPaths } from '../../lib/checkoutPaths';
import classNames from 'classnames';

type StartPaypalCheckoutMutationFn = MutationFunction<
  CoreCheckoutStartPaypalCheckout.Mutation,
  CoreCheckoutStartPaypalCheckout.Variables
>;

interface MutationProps {
  startPaypalCheckout: StartPaypalCheckoutMutationFn;
}

interface ExternalProps {
  currency: string;
  quantity?: number;
  checkoutBundlingId: string;
  onError: (errors: Error[]) => void;
  parentComponentName: string;
  skipPayPalProvider?: boolean;
  amountCents: number;
  showPaypalPayLaterButton: boolean;
  payLaterOnly?: boolean;
  inline?: boolean;
}

type Props = ChildProps<ExternalProps & MutationProps>;

export function ExpressPayPalButton({
  parentComponentName,
  currency,
  checkoutBundlingId,
  onError,
  startPaypalCheckout: startPaypalCheckoutMutation,
  quantity = null,
  skipPayPalProvider = false,
  amountCents,
  showPaypalPayLaterButton = false,
  payLaterOnly = false,
  inline = false,
}: Props) {
  const [startPaypalCheckout, { errors }] = useHOCMutation(startPaypalCheckoutMutation);
  const checkoutRef = React.useRef('');
  const buttonHeight = 48; // 4.8rem

  React.useEffect(() => {
    onError([...errors]);
  }, [onError, errors]);

  const trackPaypalEvent = useEventTracker({
    listingCurrency: currency,
    parentComponentName,
    currency,
    quantity,
    amountCents,
  });

  async function createOrder({ paymentSource }) {
    if (paymentSource === 'paylater') {
      trackPaypalEvent(MParticleEventName.ClickedPaypalPayLaterButton);
    } else {
      trackPaypalEvent(MParticleEventName.ClickedExpressPaypalButton);
    }

    const result = await startPaypalCheckout({
      variables: {
        input: {
          paymentMethodType: PayPalPaymentMethodType.DC_VIA_PAYPAL,
          checkoutBundlingId,
          quantity,
        },
      },
    });

    if (result?.data?.startPaypalCheckout.paypalOrderId) {
      checkoutRef.current = result.data.startPaypalCheckout.checkout?.id;
    }

    return result?.data?.startPaypalCheckout.paypalOrderId;
  }

  async function onApprove(data: OnApproveData) {
    trackPaypalEvent(MParticleEventName.ApprovedExpressPaypalPayment);

    const checkoutPaths = buildCheckoutPaths(checkoutRef.current);

    await Location.assign(checkoutPaths.review({ token: data.orderID }));
  }

  function onPaypalError() {
    const error = { message: I18n.t('discovery.checkout.paypal.error') };
    onError([error]);
  }

  const options = {
    currency,
    clientId: PAYPAL_CLIENT_ID,
    intent: 'capture',
    dataNamespace: `${parentComponentName}-${checkoutBundlingId}`,
    components: ['buttons', 'funding-eligibility'],
    commit: false,
    dataUid: uuid(),
  };

  const classes = classNames('', {
    'express-paypal-button': showPaypalPayLaterButton,
    'express-paypal-button--inline': inline,
  });

  return (
    <PayPalProvider
      skipProvider={skipPayPalProvider}
      options={options}
    >
      <div className={classes}>
        {!payLaterOnly &&
          <PayPalButtons
            fundingSource="paypal"
            createOrder={createOrder}
            onApprove={onApprove}
            onError={onPaypalError}
            style={{
              shape: 'pill',
              height: buttonHeight,
              layout: 'horizontal',
              tagline: false,
              color: 'gold',
            }}
          />
        }
        {showPaypalPayLaterButton &&
          <div className="express-paypal-button__pay-later">
            <PayPalButtons
              fundingSource="paylater"
              createOrder={createOrder}
              onApprove={onApprove}
              onError={onPaypalError}
              style={{
                shape: 'pill',
                height: buttonHeight,
                layout: 'horizontal',
                tagline: false,
                color: 'gold',
              }}
            />
          </div>
        }
      </div>
    </PayPalProvider>
  );
}

function PayPalProvider({ skipProvider, options, children }: {
  skipProvider: boolean,
  options: ReactPayPalScriptOptions,
  children: React.ReactElement,
}) {
  if (skipProvider) {
    return children;
  }

  return (
    <PayPalScriptProvider options={options}>
      {children}
    </PayPalScriptProvider>
  );
}

export function isCartEligible(attributes: {
  currencyCount: number,
  bundleCount: number,
  shopCount: number,
  isLocalPickupOnly: boolean,
  includesStandalonePaypal: boolean,
}) {
  const { currencyCount, bundleCount, shopCount, isLocalPickupOnly, includesStandalonePaypal } = attributes;

  return currencyCount === 1 && bundleCount === 1 && shopCount === 1 && !isLocalPickupOnly && !includesStandalonePaypal;
}

const withStartPaypalCheckoutMutation = withGraphql<
  ExternalProps,
  CoreCheckoutStartPaypalCheckout.Mutation,
  CoreCheckoutStartPaypalCheckout.Variables
>(
  gql`
  mutation Core_Checkout_StartPaypalCheckout(
    $input: Input_core_apimessages_StartPaypalCheckoutRequest
  ) {
    startPaypalCheckout(input: $input) {
      checkout {
        _id
        id
      }
      paypalOrderId
    }
  }`,
  {
    name: 'startPaypalCheckout',
  },
);

export default connect<ExternalProps>([
  withStartPaypalCheckoutMutation,
])(ExpressPayPalButton);
